import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Heart, ChevronDown } from 'lucide-react';
import { MemoryCard } from '../components/MemoryCard';
import { memories, stats } from '../data/memories';

export const HomePage = () => {
  const [counters, setCounters] = useState(stats.map(() => 0));
  const featuredMemories = memories.slice(0, 3);

  useEffect(() => {
    // Animate counters
    const duration = 2000;
    const steps = 60;
    const interval = duration / steps;

    stats.forEach((stat, index) => {
      if (stat.number === '∞') {
        setCounters(prev => {
          const newCounters = [...prev];
          newCounters[index] = '∞';
          return newCounters;
        });
        return;
      }

      const target = parseInt(stat.number);
      let current = 0;
      const increment = target / steps;

      const timer = setInterval(() => {
        current += increment;
        if (current >= target) {
          current = target;
          clearInterval(timer);
        }
        setCounters(prev => {
          const newCounters = [...prev];
          newCounters[index] = Math.floor(current);
          return newCounters;
        });
      }, interval);
    });
  }, []);

  const scrollToContent = () => {
    window.scrollTo({
      top: window.innerHeight,
      behavior: 'smooth'
    });
  };

  return (
    <div className="min-h-screen" data-testid="home-page">
      {/* Hero Section */}
      <section className="hero-section relative" data-testid="hero-section">
        {/* Background Image */}
        <div className="absolute inset-0">
          <img
            src="/images/homepage-hero.jpg"
            alt="Wedding couple"
            className="w-full h-full object-cover"
          />
          <div className="hero-overlay" />
        </div>

        {/* Content */}
        <div className="relative z-10 text-center px-4 max-w-4xl mx-auto">
          {/* Decorative Element */}
          <div className="flex items-center justify-center gap-4 mb-8 animate-fade-in">
            <div className="w-16 h-px bg-[#D4AF37]" />
            <Heart className="w-6 h-6 text-[#D4AF37]" fill="#D4AF37" />
            <div className="w-16 h-px bg-[#D4AF37]" />
          </div>

          {/* Subtitle */}
          <p className="text-[#D4AF37] text-sm md:text-base tracking-[0.3em] uppercase mb-4 animate-fade-in-up opacity-0 delay-100">
            Welcome to Our First Year
          </p>

          {/* Main Title */}
          <h1 className="font-['Playfair_Display'] text-4xl md:text-6xl lg:text-7xl text-[#F5F5F0] leading-tight mb-6 animate-fade-in-up opacity-0 delay-200">
            One Year.
            <br />
            <span className="italic text-[#D4AF37]">Countless Memories.</span>
            <br />
            A Lifetime To Go.
          </h1>

          {/* Description */}
          <p className="text-[#A18E90] text-base md:text-lg max-w-2xl mx-auto mb-10 leading-relaxed animate-fade-in-up opacity-0 delay-300">
            This website is a little corner of the internet dedicated to the most beautiful year of my life — our first year of marriage.
          </p>

          {/* CTA Button */}
          <Link
            to="/memories"
            className="btn-primary inline-block animate-fade-in-up opacity-0 delay-400"
            data-testid="explore-story-btn"
          >
            Explore Our Story
          </Link>
        </div>

        {/* Scroll Indicator */}
        <button
          className="absolute bottom-8 left-1/2 -translate-x-1/2 text-[#D4AF37] animate-bounce"
          onClick={scrollToContent}
          aria-label="Scroll down"
          data-testid="scroll-indicator"
        >
          <ChevronDown className="w-8 h-8" />
        </button>
      </section>

      {/* Stats Section */}
      <section className="py-20 md:py-32 bg-[#0F0505]" data-testid="stats-section">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="text-center mb-16">
            <p className="text-[#D4AF37] text-sm tracking-[0.2em] uppercase mb-4">Our Journey in Numbers</p>
            <h2 className="font-['Playfair_Display'] text-3xl md:text-4xl text-[#F5F5F0]">
              365 Days of Love
            </h2>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 md:gap-12">
            {stats.map((stat, index) => (
              <div key={index} className="text-center" data-testid={`stat-${index}`}>
                <div className="stat-number">
                  {counters[index]}
                </div>
                <div className="stat-label">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Featured Memories Section */}
      <section className="py-20 md:py-32 bg-[#1A0F0F]/50" data-testid="featured-memories-section">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          {/* Section Header */}
          <div className="text-center mb-16">
            <p className="text-[#D4AF37] text-sm tracking-[0.2em] uppercase mb-4">Featured Memories</p>
            <h2 className="font-['Playfair_Display'] text-3xl md:text-4xl text-[#F5F5F0] mb-4">
              Highlights From Our Year
            </h2>
            <div className="w-16 h-0.5 bg-[#D4AF37] mx-auto" />
          </div>

          {/* Memory Cards Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
            {featuredMemories.map((memory) => (
              <MemoryCard key={memory.id} memory={memory} />
            ))}
          </div>

          {/* View All Link */}
          <div className="text-center mt-12">
            <Link
              to="/memories"
              className="btn-secondary inline-block"
              data-testid="view-all-memories-btn"
            >
              View All Memories
            </Link>
          </div>
        </div>
      </section>

      {/* Quote Section */}
      <section className="py-20 md:py-32 bg-[#0F0505]" data-testid="quote-section">
        <div className="max-w-4xl mx-auto px-4 md:px-8 text-center">
          <Heart className="w-8 h-8 text-[#D4AF37] mx-auto mb-8" fill="#D4AF37" />
          <blockquote className="font-['Cormorant_Garamond'] text-2xl md:text-3xl lg:text-4xl text-[#A18E90] italic leading-relaxed mb-8">
            "Every day this year has reminded me that marrying you was the best decision I have ever made."
          </blockquote>
          <div className="w-16 h-px bg-[#D4AF37] mx-auto mb-6" />
          <p className="text-[#D4AF37] text-sm tracking-widest uppercase">
            To My Forever Love
          </p>
        </div>
      </section>
    </div>
  );
};
